import { VenueLayoutTableSize } from '../constants/venueLayoutTable'

export type FloorplanV2StageLayout = {
  stageWidth: number
  stageHeight: number
  imageWidth: number
  imageHeight: number
  scale: number
}

const DEFAULT_IMAGE_SIZE = 1200

export function resolveV2StageLayout(args: {
  containerWidth: number
  containerHeight?: number | null
  imageNaturalWidth?: number | null
  imageNaturalHeight?: number | null
}): FloorplanV2StageLayout {
  const imageNaturalWidth =
    args.imageNaturalWidth && args.imageNaturalWidth > 0
      ? args.imageNaturalWidth
      : DEFAULT_IMAGE_SIZE
  const imageNaturalHeight =
    args.imageNaturalHeight && args.imageNaturalHeight > 0
      ? args.imageNaturalHeight
      : DEFAULT_IMAGE_SIZE
  const containerWidth = Math.max(1, Math.floor(args.containerWidth) || 1)
  const containerHeight =
    args.containerHeight != null && args.containerHeight > 0
      ? Math.max(1, Math.floor(args.containerHeight))
      : null

  const scale =
    containerHeight != null
      ? Math.min(
          containerWidth / imageNaturalWidth,
          containerHeight / imageNaturalHeight
        )
      : containerWidth / imageNaturalWidth
  const imageWidth = Math.max(1, Math.round(imageNaturalWidth * scale))
  const imageHeight = Math.max(1, Math.round(imageNaturalHeight * scale))

  return {
    stageWidth: imageWidth,
    stageHeight: imageHeight,
    imageWidth,
    imageHeight,
    scale,
  }
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(100, value))
}

export function percentToStagePoint(
  xPos: number,
  yPos: number,
  layout: Pick<FloorplanV2StageLayout, 'stageWidth' | 'stageHeight'>
): { x: number; y: number } {
  const nextX = (clampPercent(xPos) / 100) * layout.stageWidth
  const nextY = (clampPercent(yPos) / 100) * layout.stageHeight
  return {
    x: Number.isFinite(nextX) ? nextX : 0,
    y: Number.isFinite(nextY) ? nextY : 0,
  }
}

export type FloorplanV2ViewportTransform = {
  panX: number
  panY: number
  zoom: number
}

/** Stage-local percent position after pan/zoom (pixels within the stage element). */
export function percentToViewportPoint(
  xPos: number,
  yPos: number,
  layout: Pick<FloorplanV2StageLayout, 'stageWidth' | 'stageHeight'>,
  viewport: FloorplanV2ViewportTransform
): { x: number; y: number } {
  const stagePoint = percentToStagePoint(xPos, yPos, layout)
  const zoom = Number.isFinite(viewport.zoom) ? viewport.zoom : 1
  return {
    x: stagePoint.x * zoom + viewport.panX,
    y: stagePoint.y * zoom + viewport.panY,
  }
}

export function resolveV2StageOffset(
  containerWidth: number,
  containerHeight: number,
  layout: Pick<FloorplanV2StageLayout, 'stageWidth' | 'stageHeight'>
): { x: number; y: number } {
  const safeContainerWidth = Math.max(1, Math.floor(containerWidth) || 1)
  const safeContainerHeight = Math.max(1, Math.floor(containerHeight) || 1)
  return {
    x: Math.max(0, (safeContainerWidth - layout.stageWidth) / 2),
    y: Math.max(0, (safeContainerHeight - layout.stageHeight) / 2),
  }
}

export function percentToContainerPoint(
  xPos: number,
  yPos: number,
  layout: Pick<FloorplanV2StageLayout, 'stageWidth' | 'stageHeight'>,
  viewport: FloorplanV2ViewportTransform,
  stageOffset: { x: number; y: number }
): { x: number; y: number } {
  const stagePoint = percentToViewportPoint(xPos, yPos, layout, viewport)
  return {
    x: stagePoint.x + stageOffset.x,
    y: stagePoint.y + stageOffset.y,
  }
}

export function stagePointToPercent(
  x: number,
  y: number,
  layout: Pick<FloorplanV2StageLayout, 'stageWidth' | 'stageHeight'>
): { xPos: number; yPos: number } {
  const xPos =
    layout.stageWidth > 0 ? clampPercent((x / layout.stageWidth) * 100) : 0
  const yPos =
    layout.stageHeight > 0 ? clampPercent((y / layout.stageHeight) * 100) : 0
  return {
    xPos,
    yPos,
  }
}

export function resolveLegacyTableMarkerSizePx(
  size: VenueLayoutTableSize
): number {
  if (size === VenueLayoutTableSize.SMALL) return 28
  if (size === VenueLayoutTableSize.LARGE) return 50
  return 40
}

/** Matches tl-web VenueMap CSS `@media (min-width: 640px)` breakpoint. */
export const V2_MARKER_COMPACT_MAX_WIDTH_PX = 640

export type ResolveV2MarkerRadiusOptions = {
  /** Floorplan viewport width; below compact max uses smaller mobile radii. */
  containerWidth?: number | null
  /** Disable compact viewport marker fallback sizing. */
  disableCompactSizing?: boolean
  /**
   * Stage/image fit scale from `resolveV2StageLayout().scale`.
   * Use this to keep marker size proportional to the floorplan image.
   */
  layoutScale?: number | null
}

/**
 * Marker radius in stage pixels.
 * Shrinks on narrow viewports to match consumer map CSS sizes, and can be
 * scaled with `layoutScale` so marker size stays proportional to the image.
 */
export function resolveV2MarkerRadiusPx(
  size: VenueLayoutTableSize,
  options?: ResolveV2MarkerRadiusOptions
): number {
  const containerWidth = options?.containerWidth
  const disableCompactSizing = options?.disableCompactSizing ?? false
  const isCompact =
    !disableCompactSizing &&
    containerWidth != null &&
    containerWidth > 0 &&
    containerWidth < V2_MARKER_COMPACT_MAX_WIDTH_PX

  if (isCompact) {
    // Matches apps/tl-web VenueMap `.markerSize*` mobile diameters (12/16/20).
    if (size === VenueLayoutTableSize.SMALL) return 6
    if (size === VenueLayoutTableSize.LARGE) return 10
    return 8
  }

  const baseRadius = resolveLegacyTableMarkerSizePx(size) / 2
  const layoutScale =
    options?.layoutScale != null && options.layoutScale > 0
      ? options.layoutScale
      : 1

  return Math.max(4, baseRadius * layoutScale)
}
